import { Component, computed, inject, signal } from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog';
import { TranslocoPipe } from '@jsverse/transloco';
import { LvrButton } from '../lvr-button/lvr-button';
import { LvrField } from '../field/field';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { LvrInputDirective } from '../../directives/lvr-input.directive';

export interface GenericDeleteDialogData {
	titleKey: string;
	messageKey?: string;
	confirmKey?: string;
	cancelKey?: string;
	contextLabel?: string;
	messageParams?: Record<string, unknown>;
	confirmInputText?: string;
	confirmInputLabelKey?: string;
	confirmInputPlaceholderKey?: string;
	confirmInputPlaceholderParams?: Record<string, unknown>;
	loadingLabel?: string;
	onConfirm?: () => void;
	onError?: () => void;
}

@Component({
	selector: 'lvr-generic-delete-dialog',
	imports: [
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatDialogClose,
		TranslocoPipe,
		LvrButton,
		LvrField,
		MatFormField,
		MatInput,
		FormsModule,
		LvrInputDirective,
	],
	templateUrl: './generic-delete-dialog.html',
})
export class GenericDeleteDialog {
	public readonly dialogRef = inject<MatDialogRef<GenericDeleteDialog>>(MatDialogRef);
	public readonly data = inject<GenericDeleteDialogData>(MAT_DIALOG_DATA);

	public readonly isLoading = signal(false);
	public readonly confirmationInput = signal('');
	public readonly isConfirmEnabled = computed(() => {
		const expected = this.data.confirmInputText?.trim();
		if (!expected) return true;
		return this.confirmationInput().trim() === expected;
	});

	public async confirm() {
		if (this.isLoading() || !this.isConfirmEnabled()) return;

		if (!this.data.onConfirm) {
			this.dialogRef.close(true); // Existing confirmation-only dialogs.
			return;
		}

		this.isLoading.set(true);

		try {
			await this.data.onConfirm();
			this.dialogRef.close(true);
		} catch {
			this.data.onError?.();
		} finally {
			this.isLoading.set(false);
		}
	}
}
